/**
 * GlobalView — Selection Handler (§14)
 *
 * Handles entity picking on the CesiumJS globe.
 * Supports both Cesium.Entity and PointPrimitive picking.
 * Click → identify entity → highlight → update globeStore.
 */

import * as Cesium from 'cesium';

interface SelectionCallbacks {
  onSelect: (entityId: string | null) => void;
  onHover: (hovering: boolean) => void;
}

/**
 * Extract entity ID from a pick result.
 * Handles both Cesium.Entity (with `.id`) and PointPrimitive/Label (with `.id` as string).
 */
function extractEntityId(picked: unknown): string | null {
  if (!Cesium.defined(picked)) return null;

  const obj = picked as Record<string, unknown>;
  let rawId: string | null = null;

  // Cesium.Entity — has .id as Cesium.Entity with .id string
  if (obj.id instanceof Cesium.Entity) {
    rawId = typeof obj.id.id === 'string' ? obj.id.id : null;
  }

  // PointPrimitive or Label — has .primitive with .id as string
  if (!rawId) {
    const primitive = obj.primitive as Record<string, unknown> | undefined;
    if (primitive && typeof primitive.id === 'string') {
      rawId = primitive.id;
    }
  }

  // Direct .id string (some Cesium primitives)
  if (!rawId && typeof obj.id === 'string') {
    rawId = obj.id;
  }

  if (!rawId) return null;

  // ── Resolve zone wall beam IDs → real entity ID ──
  // zone-beam-zone-ch-ctr-zurich-b5 → zone-ch-ctr-zurich
  if (rawId.startsWith('zone-beam-')) {
    const m = rawId.match(/^zone-beam-(zone-.+)-b\d+$/);
    return m ? m[1] : null;
  }

  // zone-base-zone-... → zone-...
  if (rawId.startsWith('zone-base-')) {
    return rawId.replace('zone-base-', '');
  }

  // ── Resolve earthquake wall/ground/point IDs → real entity ID ──
  // eq-USGS_ID-r0-b0 → USGS_ID (wall ring band)
  if (rawId.startsWith('eq-') && !rawId.startsWith('eq-gnd-') && !rawId.startsWith('eq-pt-')) {
    const m = rawId.match(/^eq-(.+)-r\d+-b\d+$/);
    return m ? m[1] : null;
  }

  // eq-gnd-USGS_ID → USGS_ID (ground glow)
  if (rawId.startsWith('eq-gnd-')) {
    return rawId.replace('eq-gnd-', '');
  }

  // eq-pt-USGS_ID → USGS_ID (center point)
  if (rawId.startsWith('eq-pt-')) {
    return rawId.replace('eq-pt-', '');
  }

  // ── Resolve fire entity IDs → real entity ID ──
  // fire-firms-47.12345-8.54321-2026-03-13-1345 → firms-47.12345-...
  if (rawId.startsWith('fire-')) {
    return rawId.replace('fire-', '');
  }

  // ── Resolve EONET natural event IDs → real entity ID ──
  // beam-eonet-EONET_12345 → eonet-EONET_12345 (vertical beam)
  if (rawId.startsWith('beam-')) {
    return rawId.replace('beam-', '');
  }

  // eonet-EONET_12345 → passes through as-is (point + label)

  return rawId;
}

/**
 * Install click + hover handlers on the CesiumJS viewer.
 *
 * @returns Destroy function
 */
export function installSelectionHandler(
  viewer: Cesium.Viewer,
  callbacks: SelectionCallbacks,
): () => void {
  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  // ─── Click → Select ─────────────────────────────────────────

  handler.setInputAction((click: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
    const picked = viewer.scene.pick(click.position);
    const entityId = extractEntityId(picked);

    if (entityId) {
      callbacks.onSelect(entityId);
    } else {
      // Deselect
      callbacks.onSelect(null);
      viewer.selectedEntity = undefined;
    }
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  // ─── Hover → Cursor feedback ────────────────────────────────

  handler.setInputAction((movement: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
    const picked = viewer.scene.pick(movement.endPosition);
    const entityId = extractEntityId(picked);

    callbacks.onHover(entityId !== null);

    // Change cursor style
    const canvas = viewer.scene.canvas;
    canvas.style.cursor = entityId !== null ? 'pointer' : 'default';
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  return () => handler.destroy();
}
